// frontend/src/components/Questionnaires/ModernQuestionnaire.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Divider,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Science as ScienceIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Code as CodeIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  MenuBook as MenuBookIcon,
  Psychology as PsychologyIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';

import {
  EDUCATION_LEVELS,
  POSITIONS,
  ACADEMIC_DEGREES,
  COMPETENCIES,
  METHODOLOGIES,
  PUBLICATION_TYPES,
  COLLABORATION_TYPES,
  POPULAR_RESEARCH_AREAS,
  searchScienceData
} from '../../data/scienceData';

const STEPS = ['Образование', 'Научные интересы', 'Компетенции', 'Опыт', 'Сотрудничество'];

const STEP_ICONS = {
  0: <SchoolIcon />,
  1: <ScienceIcon />,
  2: <CodeIcon />,
  3: <MenuBookIcon />,
  4: <GroupIcon />
};

const ModernQuestionnaire = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Состояния формы
  const [formData, setFormData] = useState({
    branch: '',
    branch_name: '',
    study_program: '',
    study_program_level: '',
    position: '',
    academic_degree: '',
    department: '',

    research_fields: [],
    methodologies: [],

    competencies: [],

    publications_count: 0,
    publication_types: [],
    projects_experience: '',

    collaboration_types: []
  });

  // Справочники из БД
  const [branchesFromDB, setBranchesFromDB] = useState([]);
  const [researchFieldsFromDB, setResearchFieldsFromDB] = useState([]);
  const [methodologiesFromDB, setMethodologiesFromDB] = useState([]);
  const [competenciesFromDB, setCompetenciesFromDB] = useState([]);
  const [publicationTypesFromDB, setPublicationTypesFromDB] = useState([]);
  const [collaborationTypesFromDB, setCollaborationTypesFromDB] = useState([]);

  const [selectedStudyPrograms, setSelectedStudyPrograms] = useState([]);
  const [studyProgramsFromDB, setStudyProgramsFromDB] = useState([]);
  const [departmentsFromDB, setDepartmentsFromDB] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  // Фильтрация программ по выбранному филиалу
    const filteredStudyPrograms = studyProgramsFromDB.filter((program) => {
      if (!formData.branch) return false;

      const programBranchId =
        typeof program.branch === 'object'
          ? program.branch?.id
          : program.branch;

      return String(programBranchId) === String(formData.branch);
    });

  const [selectedProgramName, setSelectedProgramName] = useState('');
  const [availableLevels, setAvailableLevels] = useState([]);
  const [selectedProgramLevel, setSelectedProgramLevel] = useState('');
  const [isStaff, setIsStaff] = useState(false);
  const [errors, setErrors] = useState({});

  const [competencySearchQuery, setCompetencySearchQuery] = useState('');
  const [competencySearchResults, setCompetencySearchResults] = useState([]);
  const [selectedCompetencyCategory, setSelectedCompetencyCategory] = useState(null);
  const [categoryCompetencies, setCategoryCompetencies] = useState([]);

  // 🔥 ЗАГРУЗКА ВСЕХ СПРАВОЧНИКОВ ОДНИМ ЗАПРОСОМ
  const loadAllDictionaries = async () => {
    const token = localStorage.getItem('access_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const base = 'https://fincollab.ru/api/auth';

    try {
      const [
        branches, researchFields, methodologies, competencies,
        pubTypes, collabTypes, programs, departments
      ] = await Promise.all([
        fetch(`${base}/branches/`, { headers }).then(r => r.json()),
        fetch(`${base}/research-fields/`, { headers }).then(r => r.json()),
        fetch(`${base}/methodologies/`, { headers }).then(r => r.json()),
        fetch(`${base}/competencies/`, { headers }).then(r => r.json()),
        fetch(`${base}/publication-types/`, { headers }).then(r => r.json()),
        fetch(`${base}/collaboration-types/`, { headers }).then(r => r.json()),
        fetch(`${base}/study-programs/`, { headers }).then(r => r.json()),
        fetch(`${base}/departments/`, { headers }).then(r => r.json()),
      ]);

      setBranchesFromDB(branches);
      setResearchFieldsFromDB(researchFields);
      setMethodologiesFromDB(methodologies);
      setCompetenciesFromDB(competencies);
      setPublicationTypesFromDB(pubTypes);
      setCollaborationTypesFromDB(collabTypes);
      setStudyProgramsFromDB(programs);
      setDepartmentsFromDB(departments);

      console.log('✅ Все справочники загружены');
    } catch (err) {
      console.error('❌ Ошибка загрузки справочников:', err);
    }
  };

  // Загрузка данных пользователя
  useEffect(() => {
    loadAllDictionaries();
    loadUserProfile();
  }, []);

  useEffect(() => {
    if (selectedCompetencyCategory && COMPETENCIES[selectedCompetencyCategory]) {
      setCategoryCompetencies(COMPETENCIES[selectedCompetencyCategory].items);
    } else {
      setCategoryCompetencies([]);
    }
  }, [selectedCompetencyCategory]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch('https://fincollab.ru/api/auth/profile/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('📥 Загружен профиль:', userData);

        const programName = userData.study_program_detail?.name || '';
        const programLevel = userData.study_program_detail?.level || '';
        setSelectedProgramName(programName);
        setSelectedProgramLevel(programLevel);

        if (userData.study_programs && Array.isArray(userData.study_programs)) {
          setSelectedStudyPrograms(userData.study_programs);
        }

        if (userData.department_detail) {
          setSelectedDepartmentId(userData.department_detail.id);
        }

        setFormData({
          branch: userData.branch_detail?.id || '',
          branch_name: userData.branch_detail?.name || '',
          study_program: programName,
          study_program_level: programLevel,
          position: userData.position || '',
          academic_degree: userData.academic_degree || '',
          department: userData.department_detail?.name || '',
          research_fields: userData.research_fields || [],
          methodologies: userData.methodologies || [],
          competencies: userData.competencies || [],
          publications_count: userData.publications_count || 0,
          publication_types: userData.publication_types || [],
          projects_experience: userData.projects_experience || '',
          collaboration_types: userData.collaboration_types || []
        });

        if (userData.position) {
          const selectedPosition = POSITIONS.find(p => p.value === userData.position);
          setIsStaff(selectedPosition?.category === 'staff');
        }
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки профиля:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProgramName) {
      const levels = getLevelsForProgram(selectedProgramName);
      setAvailableLevels(levels);
      if (formData.study_program_level) {
        const levelExists = levels.some(l => l.value === formData.study_program_level);
        if (levelExists) {
          setSelectedProgramLevel(formData.study_program_level);
        } else {
          setSelectedProgramLevel('');
          handleChange('study_program_level', '');
        }
      }
    } else {
      setAvailableLevels([]);
      setSelectedProgramLevel('');
    }
  }, [selectedProgramName]);

  const getLevelsForProgram = (programName) => {
    return [];
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePositionChange = (value) => {
    handleChange('position', value);
    const selectedPosition = POSITIONS.find(p => p.value === value);
    setIsStaff(selectedPosition?.category === 'staff');
  };

  const handleArrayAdd = (field, value) => {
    if (!value) return;
    const current = formData[field] || [];
    if (!current.includes(value)) {
      handleChange(field, [...current, value]);
    }
  };

  const handleArrayRemove = (field, value) => {
    handleChange(field, (formData[field] || []).filter(item => item !== value));
  };

  const handleCompetencySearch = (query) => {
    setCompetencySearchQuery(query);
    if (query.length >= 2) {
      const results = searchScienceData(query);
      setCompetencySearchResults(results.filter(r => r.type === 'Компетенция'));
    } else {
      setCompetencySearchResults([]);
    }
  };

  const handleSelectCompetencyFromSearch = (result) => {
    handleArrayAdd('competencies', result.value);
    setCompetencySearchQuery('');
    setCompetencySearchResults([]);
  };

  const handleCategoryClick = (categoryKey) => {
    if (selectedCompetencyCategory === categoryKey) {
      setSelectedCompetencyCategory(null);
    } else {
      setSelectedCompetencyCategory(categoryKey);
    }
  };

  const handleCategoryCompetencyClick = (competency) => {
    handleArrayAdd('competencies', competency);
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 0) {
      if (!formData.branch) newErrors.branch = 'Выберите организацию';
      if (!formData.position) newErrors.position = 'Укажите должность';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // 🔥 ИСПРАВЛЕНО: подготовка данных с поиском ID по названиям
  const prepareDataForSubmit = () => {
    const validProgramIds = selectedStudyPrograms
      .map(p => p.id)
      .filter(id => id !== undefined && id !== null);

    // Функция поиска ID по названиям
    const findIds = (names, dictionary) => {
      if (!names || names.length === 0) return [];
      return names
        .map(name => dictionary.find(item => item.name === name)?.id)
        .filter(id => id !== undefined);
    };

    const dataToSend = {
      position: formData.position,
      academic_degree: formData.academic_degree,
      publications_count: parseInt(formData.publications_count) || 0,
      projects_experience: formData.projects_experience,

      branch_id: formData.branch ? parseInt(formData.branch) : null,
      study_program_ids: validProgramIds,
      department_id: selectedDepartmentId,

      // 🔥 Ищем ID по названиям из scienceData.js → БД
      research_field_ids: findIds(formData.research_fields, researchFieldsFromDB),
      methodology_ids: findIds(formData.methodologies, methodologiesFromDB),
      competency_ids: findIds(formData.competencies, competenciesFromDB),
      publication_type_ids: findIds(formData.publication_types, publicationTypesFromDB),
      collaboration_type_ids: findIds(formData.collaboration_types, collaborationTypesFromDB),
    };

    // Старые поля для обратной совместимости
    if (formData.study_program && formData.study_program_level) {
      dataToSend.study_program = formData.study_program;
      dataToSend.study_program_level = formData.study_program_level;
    }

    console.log('📤 ИТОГОВЫЙ ОБЪЕКТ ДЛЯ ОТПРАВКИ:', dataToSend);
    return dataToSend;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const dataToSend = prepareDataForSubmit();
      console.log('🚀 ОТПРАВКА НА СЕРВЕР:', JSON.stringify(dataToSend, null, 2));

      const response = await fetch('https://fincollab.ru/api/auth/profile/update/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      console.log('📥 Статус ответа:', response.status);
      const data = await response.json();
      console.log('📥 Ответ сервера:', data);

      if (response.ok) {
        console.log('✅ Профиль обновлен:', data);
        setSuccess(true);
        setTimeout(() => navigate(from, { replace: true }), 2000);
      } else {
        console.error('❌ Ошибка сервера:', data);
        setError(data.error || data.detail || 'Ошибка при сохранении');
      }
    } catch (err) {
      console.error('❌ Ошибка соединения:', err);
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Загрузка анкеты...</Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(from)} sx={{ borderRadius: 4 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight="600">
            Анкета научных компетенций
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel StepIconComponent={() => (
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    bgcolor: activeStep >= index ? 'primary.main' : 'grey.300',
                    color: 'white', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', mb: 1
                  }}>
                    {STEP_ICONS[index]}
                  </Box>
                )}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>Анкета успешно сохранена! Перенаправляем...</Alert>}

        <Paper sx={{ p: 4, borderRadius: 3 }}>
          {/* ШАГ 0: Образование */}
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h5" fontWeight="600">
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Образование и должность
              </Typography>

              <FormControl fullWidth error={!!errors.branch}>
                <Autocomplete
                  options={branchesFromDB}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={branchesFromDB.find(b => b.id === formData.branch) || null}
                  onChange={(e, newValue) => {
                      const newBranchId = newValue?.id || '';

                      handleChange('branch', newBranchId);
                      handleChange('branch_name', newValue?.name || '');

                      // Удаляем выбранные программы прежнего филиала
                      setSelectedStudyPrograms([]);
                      setSelectedProgramName('');
                      setSelectedProgramLevel('');
                    }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Организация *"
                      error={!!errors.branch}
                      helperText={errors.branch || "Выберите организацию"}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth error={!!errors.position}>
                <Autocomplete
                  options={POSITIONS}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  value={POSITIONS.find(p => p.value === formData.position) || null}
                  onChange={(e, newValue) => handlePositionChange(newValue?.value || '')}
                  groupBy={(option) => option.category === 'student' ? 'Обучающиеся' : 'Сотрудники'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Должность / Статус *"
                      error={!!errors.position}
                      helperText={errors.position}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={filteredStudyPrograms}
                  getOptionLabel={(option) => {
                    const levelLabel = EDUCATION_LEVELS.find(l => l.value === option.level)?.label || option.level;
                    return `${option.name} (${option.code}) - ${levelLabel}`;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  groupBy={(option) => {
                    const levelLabel = EDUCATION_LEVELS.find(l => l.value === option.level)?.label || option.level;
                    return levelLabel;
                  }}
                  value={selectedStudyPrograms}
                  onChange={(e, newValue) => setSelectedStudyPrograms(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={option.id} label={option.name} deleteIcon={<DeleteIcon />}
                        sx={{ borderRadius: 4 }} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Направления подготовки"
                      placeholder="Выберите направления"
                      helperText={
                          formData.branch
                            ? 'Отображаются направления выбранного филиала'
                            : 'Сначала выберите организацию'
                        }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Autocomplete
                  options={ACADEMIC_DEGREES}
                  value={formData.academic_degree || null}
                  onChange={(e, newValue) => handleChange('academic_degree', newValue || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Ученая степень (при наличии)"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                  )}
                />
              </FormControl>

              {isStaff && (
                <FormControl fullWidth>
                  <Autocomplete
                    options={departmentsFromDB}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={departmentsFromDB.find(d => d.id === selectedDepartmentId) || null}
                    onChange={(e, newValue) => setSelectedDepartmentId(newValue?.id || null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Кафедра" placeholder="Выберите кафедру"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                    )}
                  />
                </FormControl>
              )}
            </Box>
          )}

          {/* ШАГ 1: Научные интересы */}
          {activeStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h5" fontWeight="600">
                <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Научные интересы
              </Typography>

              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  freeSolo
                  options={POPULAR_RESEARCH_AREAS}
                  value={formData.research_fields}
                  onChange={(e, newValue) => handleChange('research_fields', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={index} label={option} deleteIcon={<DeleteIcon />}
                        sx={{ borderRadius: 4 }} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Научные интересы"
                      placeholder="Введите или выберите из списка"
                      helperText="Например: Искусственный интеллект, Эконометрика, Финансовые рынки"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                  )}
                />
              </FormControl>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Популярные направления:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {POPULAR_RESEARCH_AREAS.slice(0, 10).map((area) => (
                    <Chip key={area} label={area}
                      onClick={() => handleArrayAdd('research_fields', area)}
                      variant={formData.research_fields.includes(area) ? 'filled' : 'outlined'}
                      color={formData.research_fields.includes(area) ? 'primary' : 'default'}
                      size="small" sx={{ borderRadius: 4 }} />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="600">
                  Методологии и подходы
                </Typography>
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    options={METHODOLOGIES}
                    value={formData.methodologies}
                    onChange={(e, newValue) => handleChange('methodologies', newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip key={index} label={option} deleteIcon={<DeleteIcon />}
                          sx={{ borderRadius: 4 }} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Методологии" placeholder="Выберите методологии"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                    )}
                  />
                </FormControl>
              </Box>
            </Box>
          )}

          {/* ШАГ 2: Компетенции */}
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h5" fontWeight="600">
                <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Компетенции
              </Typography>

              <TextField fullWidth label="Поиск компетенций"
                value={competencySearchQuery}
                onChange={(e) => handleCompetencySearch(e.target.value)}
                placeholder="Начните вводить для поиска..."
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

              {competencySearchResults.length > 0 && (
                <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                  {competencySearchResults.map((result, index) => (
                    <Box key={index} sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => handleSelectCompetencyFromSearch(result)}>
                      <Typography variant="body2" color="primary">{result.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{result.category}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Категории компетенций:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(COMPETENCIES).map(([key, category]) => (
                    <Chip key={key} label={category.name}
                      onClick={() => handleCategoryClick(key)}
                      color={selectedCompetencyCategory === key ? 'primary' : 'default'}
                      variant={selectedCompetencyCategory === key ? 'filled' : 'outlined'}
                      size="medium" sx={{ borderRadius: 4 }} />
                  ))}
                </Box>
              </Box>

              {selectedCompetencyCategory && categoryCompetencies.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    {COMPETENCIES[selectedCompetencyCategory]?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {categoryCompetencies.map((competency) => (
                      <Chip key={competency} label={competency}
                        onClick={() => handleCategoryCompetencyClick(competency)}
                        variant={formData.competencies.includes(competency) ? 'filled' : 'outlined'}
                        color={formData.competencies.includes(competency) ? 'success' : 'default'}
                        size="small" sx={{ borderRadius: 4 }} />
                    ))}
                  </Box>
                </Box>
              )}

              {formData.competencies.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="600" color="primary">
                    Выбранные компетенции:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.competencies.map((comp) => (
                      <Chip key={comp} label={comp}
                        onDelete={() => handleArrayRemove('competencies', comp)}
                        deleteIcon={<DeleteIcon />}
                        color="primary" size="medium" sx={{ borderRadius: 4 }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ШАГ 3: Опыт */}
          {activeStep === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h5" fontWeight="600">
                <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Опыт и публикации
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField label="Количество публикаций" type="number"
                  value={formData.publications_count}
                  onChange={(e) => handleChange('publications_count', parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Если пока нет, оставьте 0"
                  sx={{ width: '200px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

                <FormControl sx={{ flexGrow: 1 }}>
                  <Autocomplete multiple options={PUBLICATION_TYPES}
                    value={formData.publication_types}
                    onChange={(e, newValue) => handleChange('publication_types', newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip key={index} label={option} deleteIcon={<DeleteIcon />}
                          sx={{ borderRadius: 4 }} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Типы публикаций" placeholder="Выберите типы"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                    )}
                  />
                </FormControl>
              </Box>

              <TextField fullWidth multiline rows={4}
                label="Опыт участия в научных проектах"
                value={formData.projects_experience}
                onChange={(e) => handleChange('projects_experience', e.target.value)}
                placeholder="Расскажите о проектах, в которых вы участвовали"
                helperText="Необязательное поле"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
            </Box>
          )}

          {/* ШАГ 4: Сотрудничество */}
          {activeStep === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h5" fontWeight="600">
                <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Предпочтения по сотрудничеству
              </Typography>

              <FormControl fullWidth>
                <Autocomplete multiple options={COLLABORATION_TYPES}
                  value={formData.collaboration_types}
                  onChange={(e, newValue) => handleChange('collaboration_types', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={index} label={option} deleteIcon={<DeleteIcon />}
                        sx={{ borderRadius: 4 }} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Типы сотрудничества"
                      placeholder="Выберите интересующие вас форматы"
                      helperText="Как вы хотите сотрудничать с другими исследователями"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />
                  )}
                />
              </FormControl>
            </Box>
          )}

          {/* Кнопки навигации */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={activeStep === 0 ? () => navigate(from) : handleBack}
              variant="outlined" startIcon={<ArrowBackIcon />} size="large" sx={{ borderRadius: 6 }}>
              {activeStep === 0 ? 'Отмена' : 'Назад'}
            </Button>

            {activeStep < STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleNext} size="large" sx={{ borderRadius: 6 }}>
                Далее
              </Button>
            ) : (
              <Button variant="contained" color="success" onClick={handleSubmit}
                disabled={saving || success}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                size="large" sx={{ borderRadius: 6 }}>
                {saving ? 'Сохранение...' : 'Сохранить'}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default ModernQuestionnaire;